import { format, differenceInDays, isPast, isToday } from 'date-fns';

export interface ApprovalCondition {
  id: string;
  policy_id: string;
  comment_id: string | null;
  created_by: string;
  condition_type: 'comment_based' | 'policy_level';
  title: string;
  description: string;
  expiry_date: string | null;
  is_approved: boolean;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export type ConditionStatus = 'pending' | 'approved' | 'expired' | 'expiring_soon';

/**
 * Check if a condition is expired
 */
export function checkConditionExpiry(condition: ApprovalCondition): boolean {
  if (!condition.expiry_date || condition.is_approved) {
    return false;
  }
  return isPast(new Date(condition.expiry_date));
}

/**
 * Check if a condition is expiring soon (within 7 days)
 */
export function checkConditionExpiringSoon(condition: ApprovalCondition): boolean {
  if (!condition.expiry_date || condition.is_approved) {
    return false;
  }
  const daysUntilExpiry = differenceInDays(new Date(condition.expiry_date), new Date());
  return daysUntilExpiry >= 0 && daysUntilExpiry <= 7;
}

/**
 * Get the status of an approval condition
 */
export function getConditionStatus(condition: ApprovalCondition): ConditionStatus {
  if (condition.is_approved) {
    return 'approved';
  }
  
  if (condition.expiry_date) {
    if (checkConditionExpiry(condition)) {
      return 'expired';
    }
    if (checkConditionExpiringSoon(condition)) {
      return 'expiring_soon';
    }
  }
  
  return 'pending';
}

/**
 * Get human-readable status text
 */
export function getStatusText(status: ConditionStatus): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'expired':
      return 'Expired';
    case 'expiring_soon':
      return 'Expiring Soon';
    case 'pending':
      return 'Pending Approval';
  }
}

/**
 * Get days until expiry (positive number) or days overdue (negative number)
 */
export function getDaysUntilExpiry(condition: ApprovalCondition): number | null {
  if (!condition.expiry_date) {
    return null;
  }
  return differenceInDays(new Date(condition.expiry_date), new Date());
}

/**
 * Format expiry date for display
 */
export function formatExpiryDate(expiryDate: string): string {
  const date = new Date(expiryDate);
  if (isToday(date)) {
    return 'Today';
  }
  const daysUntil = differenceInDays(date, new Date());
  if (daysUntil < 0) {
    return `${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? '' : 's'} ago`;
  }
  if (daysUntil === 0) {
    return 'Today';
  }
  if (daysUntil === 1) {
    return 'Tomorrow';
  }
  if (daysUntil <= 7) {
    return `In ${daysUntil} days`;
  }
  return format(date, 'MMM d, yyyy');
}

/**
 * Get color classes for status badge
 */
export function getStatusColorClasses(status: ConditionStatus): string {
  switch (status) {
    case 'approved':
      return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200';
    case 'expired':
      return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900 dark:text-red-200';
    case 'expiring_soon':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-200';
    case 'pending':
      return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200';
  }
}

/**
 * Check if user has permission to approve a condition
 * Note: This is a client-side check. Server-side validation should also be done.
 */
export function canUserApproveCondition(
  condition: ApprovalCondition,
  userId: string,
  assignedApprovers: string[],
  userRoles: string[]
): boolean {
  // User must be an assigned approver or have approver/admin role
  return (
    assignedApprovers.includes(userId) ||
    userRoles.includes('approver') ||
    userRoles.includes('admin')
  );
}

/**
 * Get text for condition expiry display
 */
export function getExpiryDisplayText(condition: ApprovalCondition): {
  text: string;
  color: string;
} {
  if (!condition.expiry_date) {
    return { text: 'No expiry date', color: 'text-muted-foreground' };
  }
  
  const status = getConditionStatus(condition);
  
  switch (status) {
    case 'expired': {
      const daysOverdue = Math.abs(getDaysUntilExpiry(condition)!);
      return {
        text: `Expired ${daysOverdue} day${daysOverdue === 1 ? '' : 's'} ago`,
        color: 'text-red-600 dark:text-red-400'
      };
    }
    case 'expiring_soon': {
      const daysUntil = getDaysUntilExpiry(condition)!;
      return {
        text: `Expires in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`,
        color: 'text-yellow-600 dark:text-yellow-400'
      };
    }
    default:
      return {
        text: `Expires ${formatExpiryDate(condition.expiry_date)}`,
        color: 'text-muted-foreground'
      };
  }
}
